import api from './api';
import { fetchAllPages } from './fetchAllPages';

/**
 * Programme Stations (lot 029) : appels des écrans Réglages et Prix.
 *
 * Un objet par domaine (`stations`, `products`, `tanks`, `pumps`, `nozzles`,
 * `attendants`, `settings`, `prices`, `annexes`, `ceilings`). Chemins et formes de
 * réponse : `backend/app/api/v1/station_setup.py` et `stations.py`.
 *
 * Ces écrans de réglage écrivent en ligne : une création porte un identifiant
 * choisi ici (clé d'idempotence du serveur), une modification une clé
 * `Idempotency-Key` ; un renvoi après coupure rend donc le même résultat.
 * Aucun chiffre n'est calculé ici : l'aperçu passe par `stationCalc.js`.
 */

const unwrap = (response) => response?.data?.data ?? response?.data;

const nouvelId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** Retire les champs absents d'un corps de requête. */
const sansVides = (corps) =>
  Object.fromEntries(Object.entries(corps || {}).filter(([, valeur]) => valeur !== undefined));

const avecCle = () => ({ headers: { 'Idempotency-Key': nouvelId() } });

/** Total d'une liste paginée par le serveur: en-tête `X-Total-Count`. */
const totalEnTete = (response) => {
  const brut = response?.headers?.['x-total-count'];
  if (brut === undefined || brut === null) return null;
  const nombre = Number.parseInt(brut, 10);
  return Number.isNaN(nombre) ? null : nombre;
};

/** Liste du crédit paginée par le serveur: `{ items, total }` (en-tête `X-Total-Count`). */
const paginee = (response) => {
  const items = unwrap(response) || [];
  return { items, total: totalEnTete(response) ?? items.length };
};

/** Liste paginée `/stations…` : `{ items, total }`. */
const pagine = (response) => ({
  items: response?.data?.data || [],
  total: response?.data?.meta?.total_items ?? (response?.data?.data || []).length,
});

export const stationsApi = {
  stations: {
    async page(params = {}) {
      return pagine(await api.get('/stations', { params }));
    },
    /** Toutes les stations (toutes les pages), pour un sélecteur. */
    async all({ includeArchived = false } = {}) {
      const { items } = await fetchAllPages((params) =>
        stationsApi.stations.page({ ...params, include_archived: includeArchived }),
      );
      return items;
    },
    async get(stationId) {
      return unwrap(await api.get(`/stations/${stationId}`));
    },
    async create(data) {
      return unwrap(await api.post('/stations', sansVides({ ...data, id: data.id || nouvelId() })));
    },
    async update(stationId, data) {
      return unwrap(await api.patch(`/stations/${stationId}`, sansVides(data)));
    },
    async archive(stationId) {
      return unwrap(await api.post(`/stations/${stationId}/archive`));
    },
  },

  products: {
    async list({ includeInactive = true } = {}) {
      return unwrap(await api.get('/station-products', { params: { include_inactive: includeInactive } })) || [];
    },
    async create(data) {
      return unwrap(await api.post('/station-products', sansVides({ ...data, id: data.id || nouvelId() })));
    },
    async update(productId, data) {
      return unwrap(await api.patch(`/station-products/${productId}`, sansVides(data)));
    },
  },

  tanks: {
    async list(stationId) {
      const { items } = await fetchAllPages(async (params) =>
        pagine(await api.get(`/stations/${stationId}/tanks`, { params })),
      );
      return items;
    },
    async create(stationId, data) {
      return unwrap(
        await api.post(`/stations/${stationId}/tanks`, sansVides({ ...data, station_id: stationId, id: data.id || nouvelId() })),
      );
    },
    async update(stationId, tankId, data) {
      return unwrap(await api.patch(`/stations/${stationId}/tanks/${tankId}`, sansVides(data)));
    },
    /** Barème `[[hauteur_cm, litres], …]` ; une liste vide le retire. */
    async setChart(tankId, points, label) {
      return unwrap(await api.put(`/stations/tanks/${tankId}/chart`, { points: points || [], label: label || null }, avecCle()));
    },
  },

  pumps: {
    async list(stationId) {
      const { items } = await fetchAllPages(async (params) =>
        pagine(await api.get(`/stations/${stationId}/pumps`, { params })),
      );
      return items;
    },
    async create(stationId, data) {
      return unwrap(
        await api.post(`/stations/${stationId}/pumps`, sansVides({ ...data, station_id: stationId, id: data.id || nouvelId() })),
      );
    },
    async update(stationId, pumpId, data) {
      return unwrap(await api.patch(`/stations/${stationId}/pumps/${pumpId}`, sansVides(data)));
    },
  },

  nozzles: {
    async list(stationId) {
      return unwrap(await api.get(`/stations/${stationId}/nozzles`)) || [];
    },
    async create(stationId, data) {
      return unwrap(await api.post(`/stations/${stationId}/nozzles`, sansVides({ ...data, id: data.id || nouvelId() })));
    },
    async update(stationId, nozzleId, data) {
      return unwrap(await api.patch(`/stations/${stationId}/nozzles/${nozzleId}`, sansVides(data)));
    },
  },

  attendants: {
    async list(stationId) {
      const { items } = await fetchAllPages(async (params) =>
        pagine(await api.get(`/stations/${stationId}/attendants`, { params })),
      );
      return items;
    },
    async create(stationId, data) {
      return unwrap(
        await api.post(`/stations/${stationId}/attendants`, sansVides({ ...data, station_id: stationId, id: data.id || nouvelId() })),
      );
    },
    async update(stationId, attendantId, data) {
      return unwrap(await api.patch(`/stations/${stationId}/attendants/${attendantId}`, sansVides(data)));
    },
  },

  settings: {
    async get(stationId) {
      return unwrap(await api.get(`/stations/${stationId}/settings`));
    },
    async update(stationId, data) {
      return unwrap(await api.patch(`/stations/${stationId}/settings`, sansVides(data), avecCle()));
    },
  },

  prices: {
    /** Prix en vigueur, prochain prix et plafond de chaque produit. */
    async active(stationId, at) {
      return unwrap(await api.get(`/stations/${stationId}/prices/active`, { params: at ? { at } : {} }));
    },
    async history(stationId, { limit = 200 } = {}) {
      return unwrap(await api.get(`/stations/${stationId}/prices/history`, { params: { limit } })) || [];
    },
    /** Publie un prix ; `effectiveAt` en ISO avec fuseau (sinon : maintenant). */
    async publish(stationId, { productId, unitPrice, effectiveAt, notes }) {
      return unwrap(
        await api.post(
          `/stations/${stationId}/prices`,
          sansVides({
            id: nouvelId(),
            product_id: productId,
            unit_price: unitPrice,
            effective_at: effectiveAt || undefined,
            notes: notes || undefined,
          }),
        ),
      );
    },
  },

  /**
   * Activités annexes (lot 036) : gaz en bouteilles consignées, lubrifiants,
   * boutique, prestations. Chaque écriture porte un identifiant choisi par
   * l'écran (`id`) : c'est la clé d'idempotence, un renvoi après coupure rend
   * la même ligne. L'écran garde cet identifiant tant que la saisie n'est pas
   * enregistrée. L'auteur vient du jeton ; le prix, du serveur.
   */
  annexes: {
    /** `{ shift, items, money_hidden }` : articles, stock, prix, poste ouvert. */
    async items(stationId) {
      return unwrap(await api.get(`/station-annexes/stations/${stationId}/items`));
    },
    /** Historique, du plus récent au plus ancien : `{ lignes, montantsMasques, total }`. */
    async movements(stationId, { productId, shiftId, kind, limit = 100, skip = 0 } = {}) {
      const response = await api.get(`/station-annexes/stations/${stationId}/movements`, {
        params: sansVides({ product_id: productId, shift_id: shiftId, kind, limit, skip }),
      });
      const lignes = response?.data?.data || [];
      return {
        lignes,
        montantsMasques: Boolean(response?.data?.meta?.money_hidden),
        total: response?.data?.meta?.total ?? totalEnTete(response) ?? lignes.length,
      };
    },
    /** Ventes annexes d'un poste (montants absents sans « Montants visibles »). */
    async totals(stationId, shiftId) {
      return unwrap(await api.get(`/station-annexes/stations/${stationId}/shifts/${shiftId}/totals`));
    },
    async createItem(data) {
      return unwrap(await api.post('/station-annexes/items', sansVides({ ...data, id: data.id || nouvelId() })));
    },
    async setTerms(productId, data) {
      return unwrap(await api.put(`/station-annexes/items/${productId}/terms`, sansVides(data), avecCle()));
    },
    /** Vente dans le poste ouvert ; `empties` = vides rendus en échange. */
    async sell(stationId, { id, shiftId, productId, quantity, empties, paymentMethod, note }) {
      return unwrap(
        await api.post(
          `/station-annexes/stations/${stationId}/sales`,
          sansVides({
            id: id || nouvelId(),
            shift_id: shiftId || undefined,
            product_id: productId,
            quantity: Number(quantity),
            empties: Number(empties || 0),
            payment_method: paymentMethod || 'CASH',
            note: note || undefined,
          }),
        ),
      );
    },
    /** Vides rendus sans achat : la consigne est remboursée. */
    async returnEmpties(stationId, { id, shiftId, productId, empties, note }) {
      return unwrap(
        await api.post(
          `/station-annexes/stations/${stationId}/empties`,
          sansVides({
            id: id || nouvelId(),
            shift_id: shiftId || undefined,
            product_id: productId,
            empties: Number(empties),
            note: note || undefined,
          }),
        ),
      );
    },
    /** Entrée de marchandise ; `empties` = vides rendus au fournisseur. */
    async receive(stationId, { id, productId, quantity, empties, reference, note }) {
      return unwrap(
        await api.post(
          `/station-annexes/stations/${stationId}/receipts`,
          sansVides({
            id: id || nouvelId(),
            product_id: productId,
            quantity: Number(quantity || 0),
            empties: Number(empties || 0),
            reference: reference || undefined,
            note: note || undefined,
          }),
        ),
      );
    },
    /** Correction motivée : écarts signés sur les articles et les vides. */
    async adjust(stationId, { id, productId, quantity, empties, reason }) {
      return unwrap(
        await api.post(
          `/station-annexes/stations/${stationId}/adjustments`,
          sansVides({
            id: id || nouvelId(),
            product_id: productId,
            quantity: Number(quantity || 0),
            empties: Number(empties || 0),
            reason,
          }),
        ),
      );
    },
  },

  ceilings: {
    async list(params = {}) {
      return unwrap(await api.get('/station-price-ceilings', { params })) || [];
    },
    async create(data) {
      return unwrap(await api.post('/station-price-ceilings', sansVides({ ...data, id: data.id || nouvelId() })));
    },
  },

  // --- Lot 030 : le poste à l'index, la caisse, la journée, le rapport --------------
  // Chemins : `backend/app/api/v1/station_shifts.py`. Chaque écriture porte sa clé
  // (identifiant choisi ici, ou `idempotency_key`) : un renvoi après coupure rend
  // le même résultat, sans second mouvement de stock.

  shifts: {
    /** Station, poste ouvert, pistolets, pompistes, prix en vigueur, dernier poste clôturé. */
    async current(stationId) {
      return unwrap(await api.get(`/stations/${stationId}/shifts/current`));
    },
    async detail(stationId, shiftId) {
      return unwrap(await api.get(`/stations/${stationId}/shifts/${shiftId}/detail`));
    },
    /** `{ label, openingCash, assignments: [{nozzle_id, attendant_id}], readingsOverride: [{nozzle_id, index, reason}] }` */
    async open(stationId, { id, label, openingCash, assignments, readingsOverride }) {
      return unwrap(
        await api.post(
          `/stations/${stationId}/shifts/open`,
          sansVides({
            id: id || nouvelId(),
            label: label || undefined,
            opening_cash: Number(openingCash) || 0,
            assignments: assignments || [],
            readings_override: readingsOverride || [],
          }),
        ),
      );
    },
    async priceReadings(stationId, shiftId, readings, idempotencyKey) {
      return unwrap(
        await api.post(`/stations/${stationId}/shifts/${shiftId}/price-readings`, {
          readings,
          idempotency_key: idempotencyKey || nouvelId(),
        }),
      );
    },
    /** Aperçu de la clôture (aucune écriture). */
    async preview(stationId, shiftId, { closingReadings, declaredPayments, declaredCashByAttendant }) {
      return unwrap(
        await api.post(`/stations/${stationId}/shifts/${shiftId}/close-preview`, {
          closing_readings: closingReadings || [],
          declared_payments: declaredPayments || {},
          declared_cash_by_attendant: declaredCashByAttendant || {},
        }),
      );
    },
    async close(stationId, shiftId, { closingReadings, declaredPayments, declaredCashByAttendant, justification, idempotencyKey }) {
      return unwrap(
        await api.post(`/stations/${stationId}/shifts/${shiftId}/close`, sansVides({
          closing_readings: closingReadings || [],
          declared_payments: declaredPayments || {},
          declared_cash_by_attendant: declaredCashByAttendant || {},
          justification: justification || undefined,
          idempotency_key: idempotencyKey || nouvelId(),
        })),
      );
    },
    async reopen(stationId, shiftId, reason) {
      return unwrap(
        await api.post(`/stations/${stationId}/shifts/${shiftId}/reopen`, { reason, idempotency_key: nouvelId() }),
      );
    },
    /** Tous les postes d'une station (toutes les pages). */
    async list(stationId) {
      const { items } = await fetchAllPages(async (params) =>
        pagine(await api.get(`/stations/${stationId}/shifts`, { params })),
      );
      return items;
    },
  },

  sales: {
    /** Vente détaillée hors espèces : ni prix ni produit (imposés par le serveur). */
    async record(stationId, data) {
      return unwrap(await api.post(`/stations/${stationId}/sales`, sansVides({ ...data, id: data.id || nouvelId() })));
    },
    async cancel(stationId, saleId, reason) {
      return unwrap(await api.post(`/stations/${stationId}/sales/${saleId}/cancel`, { reason, idempotency_key: nouvelId() }));
    },
  },

  expenses: {
    async record(stationId, data) {
      return unwrap(await api.post(`/stations/${stationId}/expenses`, sansVides({ ...data, id: data.id || nouvelId() })));
    },
  },

  activity: {
    /** Derniers événements d'une station, du plus récent au plus ancien : `{ items, amounts_hidden? }`. */
    async recent(stationId, limit = 8) {
      return unwrap(await api.get(`/stations/${stationId}/activity`, { params: { limit } }));
    },
  },
  days: {
    /** Journée locale `AAAA-MM-JJ` d'une station : postes, totaux, cuves, écarts. */
    async get(stationId, date) {
      return unwrap(await api.get(`/stations/${stationId}/days/${date}`));
    },
    async lock(stationId, date, justification) {
      return unwrap(
        await api.post(`/stations/${stationId}/days/${date}/lock`, sansVides({ justification: justification || undefined, idempotency_key: nouvelId() })),
      );
    },
    async unlock(stationId, date, reason) {
      return unwrap(await api.post(`/stations/${stationId}/days/${date}/unlock`, { reason, idempotency_key: nouvelId() }));
    },
    async validate(stationId, date, note) {
      return unwrap(
        await api.post(`/stations/${stationId}/days/${date}/validate`, sansVides({ note: note || undefined, idempotency_key: nouvelId() })),
      );
    },
  },

  reports: {
    async daily(stationId, date) {
      return unwrap(await api.get(`/stations/${stationId}/days/${date}/report`));
    },
    /** Le même rapport en PDF (fichier). */
    async dailyPdf(stationId, date) {
      const response = await api.get(`/stations/${stationId}/days/${date}/report.pdf`, { responseType: 'blob' });
      return response.data;
    },
  },

  /**
   * Crédit client, flottes, bons carburant, moyens de paiement et relevés de
   * paiement mobile (lot 033) : `backend/app/api/v1/station_credit.py`.
   * Les montants (plafond, encours, relevés, factures) ne sont rendus qu'aux
   * profils qui voient les montants ; les champs absents ne sont pas à afficher.
   */
  credit: {
    accounts: {
      async list() {
        return unwrap(await api.get('/station-credit/accounts')) || [];
      },
      /** Une page de comptes (par nom): `{ items, total }`. */
      async page(params = {}) {
        return paginee(await api.get('/station-credit/accounts', { params: sansVides(params) }));
      },
      /** Tous les comptes, page après page: les chiffres de l'en-tête et les menus ont besoin de la liste entière. */
      async all() {
        const { items } = await fetchAllPages((params) => stationsApi.credit.accounts.page(params));
        return items;
      },
      async get(clientId) {
        return unwrap(await api.get(`/station-credit/accounts/${clientId}`));
      },
      /** `creditLimit` : plafond en FCFA, `null` pour fermer le compte ; `blocked` : bloquer ou débloquer. */
      async update(clientId, { creditLimit, blocked, blockReason }) {
        return unwrap(
          await api.patch(
            `/station-credit/accounts/${clientId}`,
            sansVides({ credit_limit: creditLimit, blocked, block_reason: blockReason || undefined }),
            avecCle(),
          ),
        );
      },
    },

    vehicles: {
      async list(clientId) {
        return unwrap(await api.get('/station-credit/vehicles', { params: clientId ? { client_id: clientId } : {} })) || [];
      },
      /** Une page de véhicules: `{ items, total }`. */
      async page(clientId, params = {}) {
        return paginee(
          await api.get('/station-credit/vehicles', { params: sansVides({ ...params, client_id: clientId || undefined }) }),
        );
      },
      async create(data) {
        return unwrap(await api.post('/station-credit/vehicles', sansVides({ ...data, id: data.id || nouvelId() })));
      },
      async update(vehicleId, data) {
        return unwrap(await api.patch(`/station-credit/vehicles/${vehicleId}`, sansVides(data), avecCle()));
      },
    },

    vouchers: {
      async list(params = {}) {
        return unwrap(await api.get('/station-credit/vouchers', { params: sansVides(params) })) || [];
      },
      /**
       * Une page de bons, filtrée par le serveur: `{ items, total }`. `search` cherche le
       * numéro du bon ou le nom du client; `status` : ISSUED, USED, CANCELLED ou EXPIRED.
       */
      async page(params = {}) {
        return paginee(await api.get('/station-credit/vouchers', { params: sansVides(params) }));
      },
      /** Émet `count` bons ; `id` (clé d'idempotence de l'émission) est choisi ici. */
      async issue({ clientId, productId, count, liters, amount, expiresAt, id }) {
        return (
          unwrap(
            await api.post(
              '/station-credit/vouchers',
              sansVides({
                id: id || nouvelId(),
                client_id: clientId,
                product_id: productId || undefined,
                count,
                liters: liters || undefined,
                amount: amount || undefined,
                expires_at: expiresAt || undefined,
              }),
            ),
          ) || []
        );
      },
      async cancel(voucherId, reason) {
        return unwrap(await api.post(`/station-credit/vouchers/${voucherId}/cancel`, { reason }, avecCle()));
      },
      /** Bons à imprimer, avec leur code QR en SVG (`qr_svg`). */
      async sheet(ids) {
        return unwrap(await api.post('/station-credit/vouchers/sheet', { ids })) || [];
      },
      /**
       * Un bon scanné ou saisi : son état et `problem` (la raison de ne pas l'accepter, sinon vide).
       * Le contenu du code QR suffit ; un numéro saisi n'ouvre le bon que pour son client (`clientId`).
       */
      async lookup(code, clientId) {
        return unwrap(
          await api.get('/station-credit/vouchers/lookup', { params: sansVides({ code, client_id: clientId || undefined }) }),
        );
      },
    },

    methods: {
      async list({ activeOnly = false } = {}) {
        return unwrap(await api.get('/station-credit/payment-methods', { params: { active_only: activeOnly } })) || [];
      },
      async update(code, data) {
        return unwrap(await api.patch(`/station-credit/payment-methods/${code}`, sansVides(data), avecCle()));
      },
    },

    imports: {
      async list() {
        return unwrap(await api.get('/station-credit/payment-imports')) || [];
      },
      /** Une page de relevés importés (du plus récent au plus ancien): `{ items, total }`. */
      async page(params = {}) {
        return paginee(await api.get('/station-credit/payment-imports', { params: sansVides(params) }));
      },
      async get(importId) {
        return unwrap(await api.get(`/station-credit/payment-imports/${importId}`));
      },
      /** Importe le fichier du relevé (base64, tel quel) et le rapproche des ventes. */
      async create({ methodCode, stationId, filename, contentBase64, match = true, id }) {
        return unwrap(
          await api.post(
            '/station-credit/payment-imports',
            sansVides({
              id: id || nouvelId(),
              method_code: methodCode,
              station_id: stationId || undefined,
              filename: filename || undefined,
              content_base64: contentBase64,
              match,
            }),
          ),
        );
      },
      async match(importId) {
        return unwrap(await api.post(`/station-credit/payment-imports/${importId}/match`, null, avecCle()));
      },
    },

    statements: {
      /** Sans `page`, toutes les lignes (impression); avec `page` et `page_size`, une page (`lines_total` : toutes). */
      async get(clientId, year, month, params = {}) {
        return unwrap(
          await api.get(`/station-credit/accounts/${clientId}/statement`, { params: sansVides({ year, month, ...params }) }),
        );
      },
      /** Facture les ventes à crédit du mois pas encore facturées. */
      async invoice(clientId, year, month, id) {
        return unwrap(
          await api.post(`/station-credit/accounts/${clientId}/statement/invoice`, { id: id || nouvelId(), year, month }),
        );
      },
    },
  },
};

export default stationsApi;
